import api from './api';
import { HighFinish, parseHighFinishes } from '../utils/highFinishes';
import { toApiThumbnailUrl } from '../utils/apiThumbnail';
import { t } from '../i18n';

// ============================================
// Interfaces (alle neuen Felder nullable für Robustheit)
// ============================================

/** 🎯 Dart-Art (steel = Steeldart, soft = E-Dart, both = beides). */
export type DartType = 'steel' | 'soft' | 'both';

/** ✋ Wurfhand (right = Rechtshand, left = Linkshand, both = beidhändig). */
export type ThrowingHand = 'right' | 'left' | 'both';

export interface ProfilePreviewBests {
  highestFinish?: number | null;
  bestAverage?: number | null;
  most180sSession?: number | null;
  shortestLeg?: number | null;
}

export interface ProfilePreview {
  /** ADMIN-Badge aus dem Rang (2026-10-21), null bei altem Backend */
  isAdmin?: boolean | null;
  id: string;
  username: string;
  /** Anzeigename (max. 30 Zeichen, Emojis/Leerzeichen ok) — null bei altem Backend. */
  displayName?: string | null;
  image?: string | null;
  headerImage?: string | null;
  bio?: string | null;
  memberSince?: number | null;
  /** Bestleistungen (nullable — alte Backends liefern das Feld nicht) */
  personalBests?: ProfilePreviewBests | null;
  /** Aktivitäts-Badge (null = kein Badge oder altes Backend). */
  activityBadge?: ActivityBadgeLevel | null;
  /** Aktivitäts-Score (null bei altem Backend). */
  activityScore?: number | null;
  /** Bester Average (null bei altem Backend / keinem Wert). */
  bestAverage?: number | null;
  /** Höchstes Checkout (null bei altem Backend / keinem Wert). */
  highestCheckout?: number | null;
  /** Anzahl 180er (null bei altem Backend / keinem Wert). */
  total180s?: number | null;
  /** Anzahl Turniererfolge (null bei altem Backend / keinem Wert). */
  achievementsCount?: number | null;
  /** Anzahl öffentlicher Dart-Setups (null bei altem Backend). */
  setupsCount?: number | null;
  /** Vereinsname (null bei altem Backend / keinem Verein). */
  clubName?: string | null;
  /**
   * Folgt der eingeloggte Viewer diesem Profil? Backend liefert das Feld in
   * /api/community/profiles additiv nur für eingeloggte Viewer:
   * true/false = eingeloggt (fremdes Profil), null = nicht eingeloggt/eigenes
   * Profil/altes Backend.
   */
  isFollowing?: boolean | null;
  /** 🎯 Dart-Steckbrief: Jahr, seit dem gespielt wird (null bei altem Backend). */
  dartSince?: number | null;
  /** 🎯 Dart-Steckbrief: Dart-Gewicht in Gramm (null bei altem Backend). */
  dartWeight?: number | null;
  /** 🎯 Dart-Steckbrief: Dart-Art (null bei altem Backend). */
  dartType?: DartType | null;
  /** Anzahl Follower (Backend liefert immer eine Zahl; null bei altem Backend). */
  followerCount?: number | null;
  /** 🎯 Lieblings-Doppel, z. B. "D16" (null bei altem Backend / keinem Wert). */
  favoriteDouble?: string | null;
  /** 🎵 Walk-on Song als Freitext (null bei altem Backend / keinem Wert). */
  walkOnSong?: string | null;
}

export interface RecentProfilesResponse {
  profiles: ProfilePreview[];
  total: number;
  limit: number;
  offset: number;
}

export interface SetupUser {
  id: string;
  username: string;
  image?: string | null;
}

export interface PublicDartSetup {
  id: string;
  image: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  createdAt: number;
  user?: SetupUser | null;
  commentCount?: number | null;
}

export interface AllSetupsResponse {
  setups: PublicDartSetup[];
  total: number;
  limit: number;
  offset: number;
}

/** Ein Dart Room aus der Community-Übersicht (GET /api/community/dart-rooms). */
export interface CommunityDartRoom {
  id: string;
  username: string;
  /** Anzeigename — null bei altem Backend. */
  displayName?: string | null;
  /** Avatar-Dateiname des Besitzers. */
  image?: string | null;
  /** Aktivitäts-Badge des Besitzers (null = kein Badge). */
  activityBadge?: ActivityBadgeLevel | null;
  description?: string | null;
  /** Raum-Bilder als Dateinamen (kann leer sein). */
  images: string[];
  memberSince?: number | null;
  /** Letzte Raum-Änderung in ms (2026-10-08, additiv) – null bei Bestandsräumen. */
  updatedAt?: number | null;
}

/** Detail eines Dart Rooms (GET /api/community/dart-rooms/:id, 2026-09-03) */
export interface DartRoomDetail extends CommunityDartRoom {
  activityScore?: number | null;
}

export interface DartRoomsResponse {
  rooms: CommunityDartRoom[];
  total: number;
  limit: number;
  offset: number;
}

export interface PublicClubMembership {
  id?: string | null;
  clubId?: string | null;
  clubName?: string | null;
  clubSlug?: string | null;
  clubImage?: string | null;
  memberSince?: number | null;
}

export interface PublicCustomBest {
  id?: string | null;
  label?: string | null;
  value?: string | number | null;
}

export interface PublicPersonalBests {
  highestFinish?: number | null;
  bestAverage?: number | null;
  most180sSession?: number | null;
  shortestLeg?: number | null;
  customBests?: PublicCustomBest[] | null;
}

export interface PublicDartRoom {
  description?: string | null;
  images?: string[] | null;
}

export interface PublicAchievement {
  id?: string | null;
  title?: string | null;
  tournamentName?: string | null;
  placement?: string | number | null;
  /** Unix-Timestamp in Millisekunden */
  date?: number | null;
  note?: string | null;
  /** Bilder als File-Namen (max. 6 pro Erfolg). */
  images?: string[] | null;
}

/** Aktivitäts-Badge-Stufen (verdient durch Beiträge, Kommentare, Bewertungen & Likes). */
export type ActivityBadgeLevel = 'bronze' | 'silver' | 'gold' | 'platinum';

/** Eine Zeile der Punkte-Aufschlüsselung (nur count>0, vom Backend sortiert). */
export interface ActivityBreakdownEntry {
  key: string;
  count: number;
  points: number;
}

export interface PublicPlayerProfile {
  id?: string | null;
  username: string;
  /** Seiten-Baukasten (21.10.2026): Tabs/Bausteine der Profilseite, null bei altem Backend */
  pageConfig?: unknown;
  /** ADMIN-Badge aus dem Rang (2026-10-21), null bei altem Backend */
  isAdmin?: boolean | null;
  /** Anzeigename (max. 30 Zeichen, Emojis/Leerzeichen ok) — null bei altem Backend. */
  displayName?: string | null;
  /** Aktivitäts-Badge (null = kein Badge oder altes Backend). */
  activityBadge?: ActivityBadgeLevel | null;
  /** Aktivitäts-Score (null bei altem Backend). */
  activityScore?: number | null;
  /** Punkte-Aufschlüsselung (leer bei altem Backend). */
  activityBreakdown?: ActivityBreakdownEntry[] | null;
  isPrivate?: boolean | null;
  image?: string | null;
  headerImage?: string | null;
  bio?: string | null;
  /** Spitzname wie bei den Profis, z. B. "The Power" (null bei altem Backend). */
  nickname?: string | null;
  /** Walk-on Song als Freitext, z. B. "Queen – I Want It All" (null bei altem Backend). */
  walkOnSong?: string | null;
  /** Link zum Walk-on Song, nur http(s), max. 300 Zeichen (null bei altem Backend). 2026-09-28 */
  walkOnSongUrl?: string | null;
  socialMedia?: {
    instagram?: string | null;
    facebook?: string | null;
    twitter?: string | null;
    tiktok?: string | null;
    youtube?: string | null;
    // ADDITIV (2026-09-01): neue Plattformen — alle nullable (altes Backend liefert sie nicht)
    twitch?: string | null;
    discord?: string | null;
    threads?: string | null;
    snapchat?: string | null;
    kick?: string | null;
    bluesky?: string | null;
    spotify?: string | null;
    website?: string | null;
    [key: string]: any;
  } | null;
  dartSetups: PublicDartSetup[];
  clubMemberships: PublicClubMembership[];
  memberSince?: number | null;
  personalBests?: PublicPersonalBests | null;
  dartRoom?: PublicDartRoom | null;
  /** Allgemeine Bildergalerie: Datei-Ids wie dartRoom.images, max. 24 (null bei altem Backend). 2026-09-28 */
  gallery?: string[] | null;
  achievements?: PublicAchievement[] | null;
  /** High Finishes 100–170 mit Wegen (2026-10-08, additiv) */
  highFinishes?: HighFinish[] | null;
  /** Anzahl Follower (null bei altem Backend). */
  followerCount?: number | null;
  /** Anzahl gefolgter Profile (null bei altem Backend). */
  followingCount?: number | null;
  /** true/false wenn eingeloggt, null wenn nicht eingeloggt oder altes Backend. */
  isFollowing?: boolean | null;
  /** 🎯 Dart-Steckbrief: Jahr, seit dem gespielt wird (null bei altem Backend). */
  dartSince?: number | null;
  /** 🎯 Dart-Steckbrief: Dart-Art (null bei altem Backend). */
  dartType?: DartType | null;
  /** 🎯 Dart-Steckbrief: Wurfhand (null bei altem Backend). */
  throwingHand?: ThrowingHand | null;
  /** 🎯 Dart-Steckbrief: Dart-Gewicht in Gramm, 1 Dezimale (null bei altem Backend). */
  dartWeight?: number | null;
  /** 🎯 Dart-Steckbrief: Lieblings-Double, z. B. "D16" (null bei altem Backend). */
  favoriteDouble?: string | null;
  /** 🎯 Dart-Steckbrief: Lieblings-Checkout, z. B. "170: T20 T20 Bull" (null bei altem Backend). */
  favoriteCheckout?: string | null;
  /** 🎯 Dart-Steckbrief: Liga/Verband (null bei altem Backend). */
  league?: string | null;
  /** 🎯 Dart-Steckbrief: Saison-Ziel (null bei altem Backend). */
  seasonGoal?: string | null;
}

export interface FollowActionResponse {
  success: boolean;
  following?: boolean | null;
  followerCount?: number | null;
}

export interface FollowListProfile {
  username: string;
  image?: string | null;
  /** Anzeigename (null bei altem Backend / keinem Wert). */
  displayName?: string | null;
}

export interface FollowListResponse {
  profiles: FollowListProfile[];
  total: number;
  /** Follows von Usern mit privatem Profil (zählen im total, fehlen in der Liste). */
  privateCount: number;
}

export interface TournamentHistoryPlacement {
  label: string;
  rank: number | null;
}

export interface TournamentHistoryStats {
  matches: number | null;
  wins: number | null;
  legsWon: number | null;
  legsLost: number | null;
  average: number | null;
  oneEighties: number | null;
  highFinish: number | null;
}

export interface TournamentHistoryEntry {
  operationId: string;
  name: string;
  parentTournamentName: string | null;
  tournamentSlug: string | null;
  finishedAt: number | null;
  placement: TournamentHistoryPlacement;
  stats: TournamentHistoryStats;
}

export interface TournamentHistoryResponse {
  entries: TournamentHistoryEntry[];
  total: number;
}

export interface SetupComment {
  id: string;
  setupId?: string | null;
  parentCommentId?: string | null;
  depth: number;
  authorId?: string | null;
  authorName: string;
  authorAvatarUrl?: string | null;
  /** ADMIN-Badge aus dem Rang (2026-10-21), null bei altem Backend */
  authorIsAdmin?: boolean | null;
  content: string;
  replyCount: number;
  createdAt: number;
  replies?: SetupComment[];
}

export interface SetupCommentsResponse {
  comments: SetupComment[];
  total: number;
  totalAll: number;
  limit: number;
  offset: number;
}

export interface SetupRepliesResponse {
  replies: SetupComment[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================
// Service
// ============================================

class CommunityService {
  private readonly apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');
  }

  /**
   * GET /api/community/profiles
   * Lädt öffentliche Spielerprofile (Preview-Variante).
   */
  async getRecentProfiles(params: {
    limit?: number;
    offset?: number;
    search?: string;
    sort?: 'random' | 'newest' | 'oldest' | 'activity' | 'name' | 'rank';
  } = {}): Promise<RecentProfilesResponse> {
    const { limit = 12, offset = 0, search, sort = 'random' } = params;
    try {
      const query = new URLSearchParams();
      query.set('limit', String(limit));
      query.set('offset', String(offset));
      query.set('sort', sort);
      if (search && search.trim().length > 0) {
        query.set('search', search.trim());
      }
      const response = await api.get(`/api/community/profiles?${query.toString()}`);
      return this.parseProfilesResponse(response?.data);
    } catch (error) {
      console.error('Fehler beim Abrufen der Community-Profile:', error);
      return { profiles: [], total: 0, limit, offset };
    }
  }

  /**
   * GET /api/setups
   * Lädt öffentliche Dart-Setups mit eingebettetem User-Preview.
   */
  async getAllSetups(params: {
    limit?: number;
    offset?: number;
    sort?: 'random' | 'newest' | 'oldest' | 'activity' | 'name';
  } = {}): Promise<AllSetupsResponse> {
    const { limit = 50, offset = 0, sort = 'random' } = params;
    try {
      const query = new URLSearchParams();
      query.set('limit', String(limit));
      query.set('offset', String(offset));
      query.set('sort', sort);
      const response = await api.get(`/api/setups?${query.toString()}`);
      return this.parseSetupsResponse(response?.data);
    } catch (error) {
      console.error('Fehler beim Abrufen der Community-Setups:', error);
      return { setups: [], total: 0, limit, offset };
    }
  }

  /**
   * GET /api/community/dart-rooms
   * Lädt öffentliche Dart Rooms der Community.
   * Defensiv: Fehler oder altes Backend (404) → leere Liste, kein Crash.
   */
  async getDartRooms(params: {
    limit?: number;
    offset?: number;
    /** 'newest' = zuletzt geänderte Räume zuerst (Startseite, 2026-10-08); Standard = Aktivitäts-Rang */
    sort?: 'activity' | 'newest';
  } = {}): Promise<DartRoomsResponse> {
    const { limit = 12, offset = 0, sort } = params;
    try {
      const query = new URLSearchParams();
      query.set('limit', String(limit));
      query.set('offset', String(offset));
      if (sort === 'newest') query.set('sort', 'newest');
      const response = await api.get(`/api/community/dart-rooms?${query.toString()}`);
      return this.parseDartRoomsResponse(response?.data, limit, offset);
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        console.error('Fehler beim Abrufen der Dart Rooms:', error);
      }
      return { rooms: [], total: 0, limit, offset };
    }
  }

  /**
   * GET /api/player/:username
   * Lädt das öffentliche Profil eines Spielers.
   * Gibt null zurück, wenn das Profil nicht existiert.
   * Private Profile kommen mit isPrivate=true zurück.
   */
  async getPublicProfile(username: string): Promise<PublicPlayerProfile | null> {
    if (!username || username.trim().length === 0) return null;
    try {
      const response = await api.get(`/api/player/${encodeURIComponent(username.trim())}`);
      return this.parsePublicPlayerProfile(response?.data);
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        console.error('Fehler beim Abrufen des öffentlichen Profils:', error);
      }
      return null;
    }
  }

  /**
   * POST /api/player/:username/follow (auth)
   * Folgt einem Spieler. Idempotent. Wirft bei Fehlern (z. B. Selbst-Folgen 400).
   */
  async followPlayer(username: string): Promise<FollowActionResponse> {
    const response = await api.post(
      `/api/player/${encodeURIComponent(username.trim())}/follow`,
      {}
    );
    return this.parseFollowActionResponse(response?.data);
  }

  /**
   * POST /api/player/:username/unfollow (auth)
   * Entfolgt einem Spieler. Idempotent. Wirft bei Fehlern.
   */
  async unfollowPlayer(username: string): Promise<FollowActionResponse> {
    const response = await api.post(
      `/api/player/${encodeURIComponent(username.trim())}/unfollow`,
      {}
    );
    return this.parseFollowActionResponse(response?.data);
  }

  /**
   * GET /api/player/:username/followers?limit=&offset=
   * Lädt die Follower-Liste eines Spielers.
   */
  async getFollowers(
    username: string,
    params: { limit?: number; offset?: number } = {}
  ): Promise<FollowListResponse> {
    return this.fetchFollowList(username, 'followers', params);
  }

  /**
   * GET /api/player/:username/following?limit=&offset=
   * Lädt die Liste der Profile, denen ein Spieler folgt.
   */
  async getFollowing(
    username: string,
    params: { limit?: number; offset?: number } = {}
  ): Promise<FollowListResponse> {
    return this.fetchFollowList(username, 'following', params);
  }

  private async fetchFollowList(
    username: string,
    kind: 'followers' | 'following',
    params: { limit?: number; offset?: number }
  ): Promise<FollowListResponse> {
    const { limit = 30, offset = 0 } = params;
    try {
      const query = new URLSearchParams();
      query.set('limit', String(limit));
      query.set('offset', String(offset));
      const response = await api.get(
        `/api/player/${encodeURIComponent(username.trim())}/${kind}?${query.toString()}`
      );
      const source = response?.data?.data ?? response?.data ?? {};
      const profilesRaw = Array.isArray(source?.profiles) ? source.profiles : [];
      const profiles: FollowListProfile[] = profilesRaw
        .filter((p: any) => p && typeof p === 'object' && typeof p.username === 'string' && p.username)
        .map((p: any) => ({
          username: p.username,
          image: typeof p.image === 'string' && p.image.length > 0 ? p.image : null,
          displayName:
            typeof p.displayName === 'string' && p.displayName.trim().length > 0
              ? p.displayName
              : null,
        }));
      return {
        profiles,
        total: typeof source?.total === 'number' ? source.total : profiles.length,
        privateCount: typeof source?.privateCount === 'number' ? source.privateCount : 0,
      };
    } catch (error) {
      console.error(`Fehler beim Abrufen der ${kind}-Liste:`, error);
      return { profiles: [], total: 0, privateCount: 0 };
    }
  }

  /**
   * GET /api/player/:username/tournament-history?limit&offset
   * Lädt die Turnier-Historie eines Spielers. Fehler/altes Backend → leer.
   */
  async getTournamentHistory(
    username: string,
    params: { limit?: number; offset?: number } = {}
  ): Promise<TournamentHistoryResponse> {
    const { limit = 10, offset = 0 } = params;
    if (!username || username.trim().length === 0) return { entries: [], total: 0 };
    try {
      const query = new URLSearchParams();
      query.set('limit', String(limit));
      query.set('offset', String(offset));
      const response = await api.get(
        `/api/player/${encodeURIComponent(username.trim())}/tournament-history?${query.toString()}`
      );
      const source = response?.data?.data ?? response?.data ?? {};
      const entriesRaw = Array.isArray(source?.entries) ? source.entries : [];
      const entries = entriesRaw
        .map((e: any) => this.parseTournamentHistoryEntry(e))
        .filter(Boolean) as TournamentHistoryEntry[];
      return {
        entries,
        total: typeof source?.total === 'number' ? source.total : entries.length,
      };
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        console.error('Fehler beim Abrufen der Turnier-Historie:', error);
      }
      return { entries: [], total: 0 };
    }
  }

  private parseTournamentHistoryEntry(data: any): TournamentHistoryEntry | null {
    if (!data || typeof data !== 'object') return null;
    const name = typeof data.name === 'string' ? data.name.trim() : '';
    if (!name) return null;
    const placementRaw = data.placement && typeof data.placement === 'object' ? data.placement : {};
    const statsRaw = data.stats && typeof data.stats === 'object' ? data.stats : {};
    const num = (v: any): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    return {
      operationId: typeof data.operationId === 'string' ? data.operationId : '',
      name,
      parentTournamentName:
        typeof data.parentTournamentName === 'string' && data.parentTournamentName.trim().length > 0
          ? data.parentTournamentName
          : null,
      tournamentSlug:
        typeof data.tournamentSlug === 'string' && data.tournamentSlug.trim().length > 0
          ? data.tournamentSlug
          : null,
      finishedAt: num(data.finishedAt),
      placement: {
        label: typeof placementRaw.label === 'string' ? placementRaw.label : '',
        rank: num(placementRaw.rank),
      },
      stats: {
        matches: num(statsRaw.matches),
        wins: num(statsRaw.wins),
        legsWon: num(statsRaw.legsWon),
        legsLost: num(statsRaw.legsLost),
        average: num(statsRaw.average),
        oneEighties: num(statsRaw.oneEighties),
        highFinish: num(statsRaw.highFinish),
      },
    };
  }

  private parseFollowActionResponse(payload: any): FollowActionResponse {
    const source = payload?.data ?? payload ?? {};
    return {
      success: source?.success === true,
      following: typeof source?.following === 'boolean' ? source.following : null,
      followerCount: typeof source?.followerCount === 'number' ? source.followerCount : null,
    };
  }

  /**
   * GET /api/community/dart-rooms/:id (id = User-ID ODER Benutzername)
   * Detailseite /dart-room/:id (2026-09-03). 404/privat → null.
   */
  async getDartRoomById(id: string): Promise<DartRoomDetail | null> {
    if (!id) return null;
    try {
      const response = await api.get(`/api/community/dart-rooms/${encodeURIComponent(id)}`);
      const source = response?.data?.data ?? response?.data;
      const room = this.parseCommunityDartRoom(source);
      if (!room) return null;
      return { ...room, activityScore: this.parseFiniteNumber(source?.activityScore) };
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        console.error('Fehler beim Abrufen des Dart Rooms:', error);
      }
      return null;
    }
  }

  /**
   * GET /api/setups/:setupId
   * Lädt ein einzelnes Setup inkl. User-Info und Kommentar-Anzahl.
   */
  async getSetupById(setupId: string): Promise<PublicDartSetup | null> {
    if (!setupId) return null;
    try {
      const response = await api.get(`/api/setups/${encodeURIComponent(setupId)}`);
      return this.parseSetup(response?.data);
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        console.error('Fehler beim Abrufen des Setups:', error);
      }
      return null;
    }
  }

  /**
   * GET /api/setups/:setupId/comments
   * Lädt Top-Level-Kommentare inkl. der ersten Antworten.
   */
  async getSetupComments(
    setupId: string,
    params: { limit?: number; offset?: number } = {}
  ): Promise<SetupCommentsResponse> {
    const { limit = 20, offset = 0 } = params;
    try {
      const query = new URLSearchParams();
      query.set('limit', String(limit));
      query.set('offset', String(offset));
      const response = await api.get(
        `/api/setups/${encodeURIComponent(setupId)}/comments?${query.toString()}`
      );
      return this.parseCommentsResponse(response?.data, limit, offset);
    } catch (error) {
      console.error('Fehler beim Abrufen der Setup-Kommentare:', error);
      return { comments: [], total: 0, totalAll: 0, limit, offset };
    }
  }

  /**
   * GET /api/setups/comments/:commentId/replies
   * Lädt alle Antworten auf einen Kommentar.
   */
  async getCommentReplies(
    commentId: string,
    params: { limit?: number; offset?: number } = {}
  ): Promise<SetupRepliesResponse> {
    const { limit = 50, offset = 0 } = params;
    try {
      const query = new URLSearchParams();
      query.set('limit', String(limit));
      query.set('offset', String(offset));
      const response = await api.get(
        `/api/setups/comments/${encodeURIComponent(commentId)}/replies?${query.toString()}`
      );
      const source = response?.data?.data ?? response?.data ?? {};
      const repliesRaw = Array.isArray(source?.replies) ? source.replies : [];
      const replies = repliesRaw
        .map((r: any) => this.parseComment(r))
        .filter(Boolean) as SetupComment[];
      return {
        replies,
        total: typeof source?.total === 'number' ? source.total : replies.length,
        limit,
        offset,
      };
    } catch (error) {
      console.error('Fehler beim Abrufen der Antworten:', error);
      return { replies: [], total: 0, limit, offset };
    }
  }

  /**
   * POST /api/setups/:setupId/comments (auth)
   * Schreibt einen Kommentar oder eine Antwort. Wirft bei Fehlern.
   */
  async addSetupComment(
    setupId: string,
    content: string,
    parentCommentId?: string | null
  ): Promise<SetupComment | null> {
    const body: any = { content };
    if (parentCommentId) body.parentCommentId = parentCommentId;
    const response = await api.post(
      `/api/setups/${encodeURIComponent(setupId)}/comments`,
      body
    );
    return this.parseComment(response?.data);
  }

  /**
   * DELETE /api/setups/:setupId/comments/:commentId (auth)
   * Löscht einen eigenen Kommentar. Wirft bei Fehlern.
   */
  async deleteSetupComment(setupId: string, commentId: string): Promise<void> {
    await api.delete(
      `/api/setups/${encodeURIComponent(setupId)}/comments/${encodeURIComponent(commentId)}`
    );
  }

  // --------------------------------------------
  // Response-Parser (robust gegen abweichende Formate)
  // --------------------------------------------

  private parsePublicPlayerProfile(payload: any): PublicPlayerProfile | null {
    if (!payload || typeof payload !== 'object') return null;
    const source = payload.data ?? payload;
    const username = source.username || '';
    if (!username) return null;

    const setupsRaw = Array.isArray(source.dartSetups) ? source.dartSetups : [];
    const membershipsRaw = Array.isArray(source.clubMemberships) ? source.clubMemberships : [];

    return {
      id: source.id || source._id || null,
      username,
      displayName: this.parseDisplayName(source.displayName),
      activityBadge: this.parseActivityBadge(source.activityBadge),
      isAdmin: typeof source.isAdmin === 'boolean' ? source.isAdmin : null,
      // Seiten-Baukasten (21.10.2026): Tab-Konfiguration der Profilseite roh durchreichen (parsePageConfig prüft), altes Backend → null
      pageConfig: source.pageConfig && typeof source.pageConfig === 'object' ? source.pageConfig : null,
      activityScore:
        typeof source.activityScore === 'number' && Number.isFinite(source.activityScore)
          ? source.activityScore
          : null,
      activityBreakdown: this.parseActivityBreakdown(source.activityBreakdown),
      isPrivate: source.isPrivate === true,
      image: source.image ?? null,
      headerImage: source.headerImage ?? null,
      bio: source.bio ?? null,
      nickname:
        typeof source.nickname === 'string' && source.nickname.trim().length > 0
          ? source.nickname
          : null,
      walkOnSong:
        typeof source.walkOnSong === 'string' && source.walkOnSong.trim().length > 0
          ? source.walkOnSong
          : null,
      walkOnSongUrl: this.parseTrimmedString(source.walkOnSongUrl),
      socialMedia:
        source.socialMedia && typeof source.socialMedia === 'object' ? source.socialMedia : null,
      dartSetups: setupsRaw
        .map((s: any) => this.parseSetup(s))
        .filter(Boolean) as PublicDartSetup[],
      clubMemberships: membershipsRaw
        .filter((m: any) => m && typeof m === 'object')
        .map((m: any) => ({
          id: m.id ?? null,
          clubId: m.clubId ?? null,
          clubName: m.clubName ?? null,
          clubSlug: m.clubSlug ?? null,
          clubImage: m.clubImage ?? null,
          memberSince: typeof m.memberSince === 'number' ? m.memberSince : null,
        })),
      memberSince: typeof source.memberSince === 'number' ? source.memberSince : null,
      personalBests: this.parsePersonalBests(source.personalBests),
      dartRoom: this.parseDartRoom(source.dartRoom),
      gallery: this.parseGallery(source.gallery),
      achievements: this.parseAchievements(source.achievements),
      highFinishes: parseHighFinishes(source.highFinishes),
      followerCount: typeof source.followerCount === 'number' ? source.followerCount : null,
      followingCount: typeof source.followingCount === 'number' ? source.followingCount : null,
      isFollowing: typeof source.isFollowing === 'boolean' ? source.isFollowing : null,
      dartSince: this.parseFiniteNumber(source.dartSince),
      dartType: this.parseDartType(source.dartType),
      throwingHand: this.parseThrowingHand(source.throwingHand),
      dartWeight: this.parseFiniteNumber(source.dartWeight),
      favoriteDouble: this.parseTrimmedString(source.favoriteDouble),
      favoriteCheckout: this.parseTrimmedString(source.favoriteCheckout),
      league: this.parseTrimmedString(source.league),
      seasonGoal: this.parseTrimmedString(source.seasonGoal),
    };
  }

  /** Anzeigename defensiv parsen (leer/kein String → null). */
  private parseDisplayName(value: any): string | null {
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  }

  /** 🎯 Dart-Art defensiv parsen (nur bekannte Werte, sonst null — altes Backend). */
  private parseDartType(value: any): DartType | null {
    return value === 'steel' || value === 'soft' || value === 'both' ? value : null;
  }

  /** ✋ Wurfhand defensiv parsen (nur bekannte Werte, sonst null — altes Backend). */
  private parseThrowingHand(value: any): ThrowingHand | null {
    return value === 'right' || value === 'left' || value === 'both' ? value : null;
  }

  /** String defensiv parsen (nur nicht-leere Strings, sonst null). */
  private parseTrimmedString(value: any): string | null {
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  }

  /** Aktivitäts-Badge defensiv parsen (nur bekannte Stufen, sonst null). */
  private parseActivityBadge(value: any): ActivityBadgeLevel | null {
    return value === 'bronze' || value === 'silver' || value === 'gold' || value === 'platinum'
      ? value
      : null;
  }

  /** Punkte-Aufschlüsselung defensiv parsen (nur gültige Zeilen mit count>0, sonst []). */
  private parseActivityBreakdown(value: any): ActivityBreakdownEntry[] {
    if (!Array.isArray(value)) return [];
    return value
      .filter(
        (entry: any) =>
          entry &&
          typeof entry === 'object' &&
          typeof entry.key === 'string' &&
          typeof entry.count === 'number' &&
          Number.isFinite(entry.count) &&
          entry.count > 0 &&
          typeof entry.points === 'number' &&
          Number.isFinite(entry.points)
      )
      .map((entry: any) => ({ key: entry.key, count: entry.count, points: entry.points }));
  }

  private parsePersonalBests(data: any): PublicPersonalBests | null {
    if (!data || typeof data !== 'object') return null;
    const customRaw = Array.isArray(data.customBests) ? data.customBests : [];
    return {
      highestFinish: typeof data.highestFinish === 'number' ? data.highestFinish : null,
      bestAverage: typeof data.bestAverage === 'number' ? data.bestAverage : null,
      most180sSession: typeof data.most180sSession === 'number' ? data.most180sSession : null,
      shortestLeg: typeof data.shortestLeg === 'number' ? data.shortestLeg : null,
      customBests: customRaw
        .filter((c: any) => c && typeof c === 'object')
        .map((c: any) => ({
          id: c.id ?? c._id ?? null,
          label: typeof c.label === 'string' ? c.label : null,
          value:
            typeof c.value === 'string' || typeof c.value === 'number' ? c.value : null,
        })),
    };
  }

  private parseDartRoom(data: any): PublicDartRoom | null {
    if (!data || typeof data !== 'object') return null;
    const imagesRaw = Array.isArray(data.images) ? data.images : [];
    return {
      description: typeof data.description === 'string' ? data.description : null,
      images: imagesRaw.filter((img: any) => typeof img === 'string' && img.length > 0),
    };
  }

  /** Galerie (2026-09-28) defensiv: kein Array → null (altes Backend), sonst nur nicht-leere Strings, max. 24. */
  private parseGallery(data: any): string[] | null {
    if (!Array.isArray(data)) return null;
    return data.filter((img: any) => typeof img === 'string' && img.length > 0).slice(0, 24);
  }

  private parseAchievements(data: any): PublicAchievement[] | null {
    if (!Array.isArray(data)) return null;
    return data
      .filter((a: any) => a && typeof a === 'object')
      .map((a: any) => ({
        id: a.id ?? a._id ?? null,
        title: typeof a.title === 'string' ? a.title : null,
        tournamentName: typeof a.tournamentName === 'string' ? a.tournamentName : null,
        placement:
          typeof a.placement === 'string' || typeof a.placement === 'number'
            ? a.placement
            : null,
        date: typeof a.date === 'number' ? a.date : null,
        note: typeof a.note === 'string' ? a.note : null,
        images: Array.isArray(a.images)
          ? a.images
              .filter((img: any) => typeof img === 'string' && img.length > 0)
              .slice(0, 6)
          : null,
      }));
  }

  private parseCommentsResponse(payload: any, limit: number, offset: number): SetupCommentsResponse {
    const source = payload?.data ?? payload ?? {};
    const commentsRaw = Array.isArray(source?.comments) ? source.comments : [];
    const comments = commentsRaw
      .map((c: any) => this.parseComment(c))
      .filter(Boolean) as SetupComment[];
    return {
      comments,
      total: typeof source?.total === 'number' ? source.total : comments.length,
      totalAll: typeof source?.totalAll === 'number' ? source.totalAll : comments.length,
      limit: typeof source?.limit === 'number' ? source.limit : limit,
      offset: typeof source?.offset === 'number' ? source.offset : offset,
    };
  }

  private parseComment(data: any): SetupComment | null {
    if (!data || typeof data !== 'object') return null;
    const id = data.id || data._id || '';
    if (!id) return null;
    const repliesRaw = Array.isArray(data.replies) ? data.replies : [];
    return {
      id,
      setupId: data.setupId ?? null,
      parentCommentId: data.parentCommentId ?? null,
      depth: typeof data.depth === 'number' ? data.depth : 0,
      authorId: data.authorId ?? null,
      authorName: data.authorName || t('web.component.comment-item.unbekannt'),
      authorAvatarUrl: data.authorAvatarUrl ?? null,
      authorIsAdmin: typeof data.authorIsAdmin === 'boolean' ? data.authorIsAdmin : null,
      content: data.content || '',
      replyCount: typeof data.replyCount === 'number' ? data.replyCount : 0,
      createdAt: typeof data.createdAt === 'number' ? data.createdAt : 0,
      replies: repliesRaw
        .map((r: any) => this.parseComment(r))
        .filter(Boolean) as SetupComment[],
    };
  }

  private parseProfilesResponse(payload: any): RecentProfilesResponse {
    if (!payload) return { profiles: [], total: 0, limit: 12, offset: 0 };
    const source = payload.data ?? payload;
    const profilesRaw = source?.profiles ?? source?.data?.profiles ?? [];
    const profiles: ProfilePreview[] = Array.isArray(profilesRaw)
      ? profilesRaw.map((p: any) => this.parseProfilePreview(p)).filter(Boolean) as ProfilePreview[]
      : [];
    return {
      profiles,
      total: typeof source?.total === 'number' ? source.total : profiles.length,
      limit: typeof source?.limit === 'number' ? source.limit : 12,
      offset: typeof source?.offset === 'number' ? source.offset : 0,
    };
  }

  private parseSetupsResponse(payload: any): AllSetupsResponse {
    if (!payload) return { setups: [], total: 0, limit: 50, offset: 0 };
    const source = payload.data ?? payload;
    const setupsRaw = source?.setups ?? source?.data?.setups ?? [];
    const setups: PublicDartSetup[] = Array.isArray(setupsRaw)
      ? setupsRaw.map((s: any) => this.parseSetup(s)).filter(Boolean) as PublicDartSetup[]
      : [];
    return {
      setups,
      total: typeof source?.total === 'number' ? source.total : setups.length,
      limit: typeof source?.limit === 'number' ? source.limit : 50,
      offset: typeof source?.offset === 'number' ? source.offset : 0,
    };
  }

  private parseProfilePreview(data: any): ProfilePreview | null {
    if (!data || typeof data !== 'object') return null;
    const id = data.id || data._id || '';
    const username = data.username || '';
    if (!username) return null;
    return {
      id,
      username,
      displayName: this.parseDisplayName(data.displayName),
      image: data.image ?? null,
      headerImage: data.headerImage ?? null,
      bio: data.bio ?? null,
      memberSince: typeof data.memberSince === 'number' ? data.memberSince : null,
      personalBests: data.personalBests && typeof data.personalBests === 'object'
        ? {
            highestFinish: data.personalBests.highestFinish ?? null,
            bestAverage: data.personalBests.bestAverage ?? null,
            most180sSession: data.personalBests.most180sSession ?? null,
            shortestLeg: data.personalBests.shortestLeg ?? null,
          }
        : null,
      activityBadge: this.parseActivityBadge(data.activityBadge),
      isAdmin: typeof data.isAdmin === 'boolean' ? data.isAdmin : null,
      activityScore: this.parseFiniteNumber(data.activityScore),
      bestAverage: this.parseFiniteNumber(data.bestAverage),
      highestCheckout: this.parseFiniteNumber(data.highestCheckout),
      total180s: this.parseFiniteNumber(data.total180s),
      achievementsCount: this.parseFiniteNumber(data.achievementsCount),
      clubName:
        typeof data.clubName === 'string' && data.clubName.trim().length > 0
          ? data.clubName.trim()
          : null,
      isFollowing: typeof data.isFollowing === 'boolean' ? data.isFollowing : null,
      dartSince: this.parseFiniteNumber(data.dartSince),
      dartWeight: this.parseFiniteNumber(data.dartWeight),
      dartType: this.parseDartType(data.dartType),
      followerCount: this.parseFiniteNumber(data.followerCount),
      favoriteDouble: this.parseTrimmedString(data.favoriteDouble),
      walkOnSong: this.parseTrimmedString(data.walkOnSong),
    };
  }

  /** Zahl defensiv parsen (nur endliche Zahlen, sonst null). */
  private parseFiniteNumber(value: any): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private parseDartRoomsResponse(payload: any, limit: number, offset: number): DartRoomsResponse {
    if (!payload) return { rooms: [], total: 0, limit, offset };
    const source = payload.data ?? payload;
    const roomsRaw = source?.rooms ?? source?.data?.rooms ?? [];
    const rooms: CommunityDartRoom[] = Array.isArray(roomsRaw)
      ? (roomsRaw.map((r: any) => this.parseCommunityDartRoom(r)).filter(Boolean) as CommunityDartRoom[])
      : [];
    return {
      rooms,
      total: typeof source?.total === 'number' ? source.total : rooms.length,
      limit: typeof source?.limit === 'number' ? source.limit : limit,
      offset: typeof source?.offset === 'number' ? source.offset : offset,
    };
  }

  private parseCommunityDartRoom(data: any): CommunityDartRoom | null {
    if (!data || typeof data !== 'object') return null;
    const username = typeof data.username === 'string' ? data.username : '';
    if (!username) return null;
    const imagesRaw = Array.isArray(data.images) ? data.images : [];
    return {
      id: data.id || data._id || username,
      username,
      displayName: this.parseDisplayName(data.displayName),
      image: typeof data.image === 'string' && data.image.length > 0 ? data.image : null,
      activityBadge: this.parseActivityBadge(data.activityBadge),
      description:
        typeof data.description === 'string' && data.description.trim().length > 0
          ? data.description
          : null,
      images: imagesRaw.filter((img: any) => typeof img === 'string' && img.length > 0),
      memberSince: this.parseFiniteNumber(data.memberSince),
      // 2026-10-08: letzte Raum-Änderung (Backend additiv, null bei alten Räumen/altem Backend)
      updatedAt: this.parseFiniteNumber(data.updatedAt),
    };
  }

  private parseSetup(data: any): PublicDartSetup | null {
    if (!data || typeof data !== 'object') return null;
    const id = data.id || data._id || '';
    const image = data.image || '';
    const title = data.title || '';
    if (!id || !image) return null;
    return {
      id,
      image,
      title,
      subtitle: data.subtitle ?? null,
      description: data.description ?? null,
      createdAt: typeof data.createdAt === 'number' ? data.createdAt : 0,
      user: this.parseSetupUser(data.user),
      commentCount: typeof data.commentCount === 'number' ? data.commentCount : null,
    };
  }

  private parseSetupUser(data: any): SetupUser | null {
    if (!data || typeof data !== 'object') return null;
    const id = data.id || data._id || '';
    const username = data.username || '';
    if (!username) return null;
    return { id, username, image: data.image ?? null };
  }

  // --------------------------------------------
  // Bild-URL Helper (analog zum Flutter CachedImage)
  // --------------------------------------------

  /**
   * Baut die Thumbnail-URL (100/200/400) analog zum Flutter-Client.
   * Externe URLs werden unverändert zurückgegeben.
   * Akzeptiert Dateinamen UND bereits relative Backend-Pfade
   * ('/api/file/…' — neuer Dart-Rooms-Kontrakt 2026-09-01).
   */
  buildThumbnailUrl(fileName?: string | null, size: 100 | 200 | 400 = 200): string | null {
    const absolute = this.buildImageUrl(fileName);
    if (absolute === null) return null;
    // Thumbnail-Ableitung zentral über die pure Funktion (fail-soft:
    // data:/extern/ohne Endung → Original bleibt unverändert).
    return toApiThumbnailUrl(absolute, size) ?? absolute;
  }

  /**
   * Baut die volle Bild-URL. Dateinamen werden auf `/api/file/<name>`
   * gemappt, RELATIVE Pfade (führendes '/', z. B. '/api/file/…' aus
   * /api/community/dart-rooms) nur mit der Backend-Base-URL absolutiert —
   * NIE doppelt geprefixt.
   */
  buildImageUrl(fileName?: string | null): string | null {
    if (typeof fileName !== 'string') return null;
    const trimmed = fileName.trim();
    if (!trimmed) return null; // leer/whitespace = kein Bild
    if (trimmed.startsWith('data:')) return trimmed;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    if (trimmed.startsWith('/')) {
      // bereits relativer Backend-Pfad → nur absolutieren
      return `${this.apiBaseUrl}${trimmed}`;
    }
    return `${this.apiBaseUrl}/api/file/${trimmed}`;
  }

  /** Formatiert einen Unix-Timestamp (ms) als relative Zeitangabe (deutsch). */
  formatRelativeTime(timestamp?: number | null): string {
    if (!timestamp || timestamp <= 0) return '';
    const diff = Date.now() - timestamp;
    if (diff < 60 * 1000) return t('web.service.comment-service.gerade-eben');
    const minutes = Math.floor(diff / (60 * 1000));
    if (minutes < 60) return t('web.service.comment-service.vor-min', { minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('web.service.comment-service.vor-std', { hours });
    const days = Math.floor(hours / 24);
    if (days < 7) return days === 1 ? t('web.service.comment-service.vor-1-tag') : t('web.service.comment-service.vor-tagen', { days });
    try {
      return new Date(timestamp).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (_) {
      return '';
    }
  }

  /** Formatiert einen Unix-Timestamp (ms) als "Mmm YYYY" (deutsch). */
  formatMemberSince(timestamp?: number | null): string | null {
    if (!timestamp || timestamp <= 0) return null;
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' });
    } catch (_) {
      return null;
    }
  }
}

const communityService = new CommunityService();
export default communityService;
